import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getBulkStoreStatus,
  setStoresUnFavourite,
  resetStores,
} from '@kfc-global/react-shared/redux/Actions/StoresAction';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';

import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import ImageComponent from 'atoms/Image';
import { createPayloadForStatusCall, getDispositionTypeOnAPIFormat, toastifyFnWithImage } from 'common/utilities/utils';
import { ADDRESS_STORE_CONSTANTS } from '../../Constants/Constants';
import { toast } from 'react-toastify';
import { UserOrderContext } from 'context/context';
import SEARCH_STORE_CONSTANTS from 'organisms/SearchStore/AU/Constants/SearchStoreConstants';
import FavouriteStoreContainer from './FavouriteStoreContainer';
import DeliveryAddressContainer from './DeliveryAddressContainer';
import { LOCAL_STORAGE_KEYS } from 'common/constants/SharedConstants';
import { getDispositionNewData } from 'organisms/MenusPLP/MenusPlpUtilities/MenuPLPSharedUtils';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import AuthManager from '@kfc-global/react-shared/auth/AuthManager';

const AddressStoresContainer = props => {
  const { MY_FAV_STORES, UNDEFINED } = ADDRESS_STORE_CONSTANTS;
  const { STORE_STATUS_OPEN } = SEARCH_STORE_CONSTANTS;
  const { NO_FAVOURITE_STORE, REMOVED_MSG } = TRANSLATE_MAPPING_KEY;
  const dispatch = useDispatch();
  const { userOrderState } = useContext(UserOrderContext);
  const { storesWithStatus, allStores } = useSelector(data => data.storesReducer);
  const { favouriteStores, savedAddresses } = useSelector(data => data.profileReducer);
  const { localized = false } = useSelector(data => data.appStateReducer?.userStatus);
  const tenantReducer = useSelector(data => data.tenantReducer);
  const { tenantId = '', storeSearch = {}, driveTime, componentOptions } = tenantReducer;
  const { web = {} } = componentOptions || {};
  const { shouldCheckDropOffAddress = false } = web;
  const [favStores, setUpdatedStore] = useState([]);
  const [removeFavStore, setRemoveFavorite] = useState(false);
  const { showSearchStore } = userOrderState.searchStoreData;
  const { showScheduleOrderCatering } = userOrderState.scheduleOrderData;

  const dispositionNew = getDispositionNewData(userOrderState);
  const params = useMemo(() => {
    return {
      dispositionType: dispositionNew?.type
        ? getDispositionTypeOnAPIFormat(dispositionNew.type)
        : getDispositionTypeOnAPIFormat(tenantReducer?.authoredContent?.defaultdisposition),
    };
  }, [dispositionNew, tenantReducer]);
  const { dispositionType } = params;

  useEffect(() => {
    if (favouriteStores?.length && tenantId && !showSearchStore && !showScheduleOrderCatering) {
      const storeIds = createPayloadForStatusCall(favouriteStores, dispositionType, null, shouldCheckDropOffAddress);
      const isFavoritePage = true;
      const favStoreSet = localStorage.getItem(LOCAL_STORAGE_KEYS.SET_FAV);
      if (!favStoreSet) {
        dispatch(getBulkStoreStatus({ data: storeIds, tenantId, isFavoritePage }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, favouriteStores?.length, savedAddresses?.length, showSearchStore, showScheduleOrderCatering, JSON.stringify(savedAddresses),
  ]);

  useEffect(() => {
    // componentDidMount events
    window.scrollTo(0, 0);
    dispatch(resetStores());
    return () => {
      dispatch(resetStores());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(resetStores());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteStores.length]);

  useEffect(() => {
    if (favStores?.length && storesWithStatus?.length) {
      storeUpdateCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesWithStatus?.length, favStores.length]);

  const storeUpdateCheck = () => {
    const fStore = favStores.map(favStore => {
      let favStoreInfo = { ...favStore };
      const timings = storesWithStatus.find(
        obj => obj.storeId === favStore.storeBasic.id || obj.id === favStore.storeBasic.id,
      );
      if (timings) {
        favStoreInfo['summary'] = timings['summary'];
        favStoreInfo['isOpen'] = timings['summary']['status']['currentStatus'] === STORE_STATUS_OPEN;
      }
      return favStoreInfo;
    });
    setUpdatedStore(fStore.filter(obj => obj.summary));
  };

  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.RENDER_COMP) && localStorage.getItem(LOCAL_STORAGE_KEYS.SET_FAV)) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.RENDER_COMP);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.SET_FAV);
      dispatch(resetStores());
      setDistance();
    }
  });

  useEffect(() => {
    setDistance();
    if (removeFavStore) {
      const imageTemp = <ImageComponent srcFile={IMAGE_PATH.heartIcon_active} />;
      toastifyFnWithImage(translateWithI18Next(REMOVED_MSG), imageTemp);
      setTimeout(() => {
        toast.dismiss();
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteStores.length, removeFavStore]);

  useEffect(() => {
    setDistance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses.length, JSON.stringify(savedAddresses)]);

  const updateStoreForFavourite = () => {
    if (favStores?.length && storesWithStatus?.length) {
      storeUpdateCheck();
    } else {
      setUpdatedStore(favouriteStores);
      setRemoveFavorite(false);
    }
  };

  const setDistance = () => {
    const getStoreInfoFromStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.STORE_INFO);
    const storeInfo =
      getStoreInfoFromStorage && getStoreInfoFromStorage !== UNDEFINED && JSON.parse(getStoreInfoFromStorage);
    const defaultAddress = savedAddresses?.length ? savedAddresses.filter(obj => obj.isPrimary) : [];
    const originInfo = storeInfo && localized ? storeInfo : defaultAddress?.length && defaultAddress[0];
    if (originInfo) {
      let distanceMetrix = storeSearch?.distanceMetrics;
      if (distanceMetrix !== ADDRESS_STORE_CONSTANTS?.KM) {
        distanceMetrix = window.google.maps.UnitSystem.IMPERIAL;
      } else {
        distanceMetrix = window.google.maps.UnitSystem.METRIC;
      }
      const allStoreLocations = favouriteStores.map(favStore => {
        const { latitude, longitude } = favStore.storeBasic?.location || {};
        return { lat: latitude, lng: longitude };
      });
      if (favouriteStores?.length) {
        getDistanceFromGoogleMatrix(
          [{ lat: originInfo?.location?.latitude, lng: originInfo?.location?.longitude }],
          allStoreLocations,
          distanceMetrix,
        );
      } else {
        setUpdatedStore([]);
        setRemoveFavorite(false);
      }
    } else {
      updateStoreForFavourite();
    }
  };

  const getDistanceFromGoogleMatrix = (startOrigin, destination, distanceMetrix) => {
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [...startOrigin].slice(0, 24),
        destinations: [...destination].slice(0, 24),
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: distanceMetrix,
        avoidHighways: false,
        avoidTolls: false,
      },
      collectAllDistance,
    );
  };

  const collectAllDistance = (response, status) => {
    let allStoreWithDistanceInfo = [];

    let allStoreDistance = [];
    allStoreDistance = response && response.rows && response.rows[0].elements.map(storeDistance => storeDistance);
    allStoreWithDistanceInfo = favouriteStores.map((store, index) => {
      return { ...store, ...allStoreDistance[index] };
    });

    setUpdatedStore(allStoreWithDistanceInfo);
    setRemoveFavorite(false);
  };

  const removeFromFavoriteHandler = id => {
    const { customerId } = AuthManager.getInstance();
    dispatch(
      setStoresUnFavourite({
        tenantId: tenantId,
        customerId: customerId,
        storeId: id,
        dispatch: 'dispatch',
      }),
    );
    setRemoveFavorite(true);
  };

  return (
    <div className='addresses-and-stores-container' id='addresses-global'>
      <div className='my-delivery-addresses-header-mobile'>
        <ImageComponent
          srcFile={IMAGE_PATH.SingleArrowback}
          className='backArrow-account-setting'
          data-testid='backArrow-account-setting'
          onClick={() => props.updateMenu('')}
        />
        <span className='account-setting-text-header'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.STOREADDRESS)}</span>
      </div>
      {favStores?.length > 0 && (
        <FavouriteStoreContainer
          data-testid='favStore-list'
          removeFromFavoriteHandler={removeFromFavoriteHandler}
          localized={localized}
          history={props.history}
          storesReducer={storesWithStatus}
          favouriteStores={favStores}
          isMobile={props.isMobile}
          showMoreInfo={true}
          savedAddresses={savedAddresses}
        />
      )}
      {favStores?.length === 0 && (
        <div className='favorite-stores-section'>
          <div className='header-text'>
            {translateWithI18Next(MY_FAV_STORES)} ({favouriteStores.length})
          </div>
          <div className='no-fav-store'>
            <p>{translateWithI18Next(NO_FAVOURITE_STORE)}</p>
          </div>
        </div>
      )}
      <DeliveryAddressContainer
        storeSearch={storeSearch}
        savedAddresses={savedAddresses}
        isMobile={props.isMobile}
        tenantId={tenantId}
        driveTime={driveTime}
        allStores={allStores}
      />
    </div>
  );
};
export default AddressStoresContainer;
